import React from 'react';

export type NeedsYourActionGroupKey = 'approvals' | 'bids' | 'incidents';

interface NeedsYourActionGroupProps {
  groupKey: NeedsYourActionGroupKey;
  icon: React.ReactNode;
  label: string;
  count: number;
  items: React.ReactNode[];
  expanded: boolean;
  onToggleExpand: () => void;
  visibleLimit?: number;
}

const DEFAULT_VISIBLE_LIMIT = 5;

/**
 * Sprint 16, story-075/076. One labeled group (icon + title + count + rows + show-more),
 * reused 3x by `NeedsYourActionBlock` with a different row renderer per group. Renders nothing
 * when its own count is 0 — each group is independently optional.
 */
export const NeedsYourActionGroup: React.FC<NeedsYourActionGroupProps> = ({
  groupKey,
  icon,
  label,
  count,
  items,
  expanded,
  onToggleExpand,
  visibleLimit = DEFAULT_VISIBLE_LIMIT,
}) => {
  if (count === 0) return null;

  const visibleItems = expanded ? items : items.slice(0, visibleLimit);
  const hasMore = items.length > visibleLimit;

  return (
    <div
      data-testid={`needs-your-action-group-${groupKey}`}
      className="py-2 border-t border-gray-100 first:border-t-0 first:pt-0">
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-800">
        {icon}
        <span>
          {label} ({count})
        </span>
      </div>
      <div className="space-y-2">{visibleItems}</div>
      {hasMore && (
        <button
          type="button"
          data-testid={`needs-your-action-show-more-${groupKey}`}
          onClick={onToggleExpand}
          className="mt-2 text-xs font-medium text-indigo-600 hover:underline">
          {expanded ? 'Show less' : `Show ${items.length - visibleLimit} more`}
        </button>
      )}
    </div>
  );
};

export default NeedsYourActionGroup;
